import React, { useState, useEffect } from 'react';
import { useAnalytics } from '../hooks/useAnalytics';
import { useAuth } from '../context/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { BarChart3, PieChart, TrendingUp } from 'lucide-react';
import { AnalyticsDashboard } from '../components/analytics/AnalyticsDashboard';
import { WishlistButton } from '../components/common/WishlistButton';

export const AnalyticsPage = () => {
    const [userEmail, setUserEmail] = useState(null);
    const [showAnalytics, setShowAnalytics] = useState(false);
    const { trackEvent, events } = useAnalytics();
    const { user } = useAuth();

    useEffect(() => {
        setUserEmail(user?.email);
        
        // Track page view
        trackEvent(events.FEATURE.PAGE_VIEW, 'feature', {
            page: 'analytics',
            user_id: user?.uid || 'unknown',
            isAnonymous: user?.is_anonymous,
            userEmail: user?.email || undefined
        });
    }, [user, trackEvent, events.FEATURE.PAGE_VIEW]);

    const handleAnalyticsToggle = () => {
        setShowAnalytics(!showAnalytics);
        trackEvent(events.FEATURE.ANALYTICS_VIEW, 'feature', {
            action: showAnalytics ? 'hide' : 'show',
            user_id: user?.uid || 'unknown',
            isAnonymous: user?.is_anonymous
        });
    };

    const features = [
        {
            icon: <BarChart3 className="w-8 h-8 text-indigo-500" />,
            title: "Spending Analytics",
            description: "Track and analyze all your subscription costs"
        },
        {
            icon: <PieChart className="w-8 h-8 text-emerald-500" />,
            title: "Budget Insights",
            description: "Understand your spending patterns"
        },
        {
            icon: <TrendingUp className="w-8 h-8 text-purple-500" />,
            title: "Cost Forecasting",
            description: "Predict future subscription expenses"
        }
    ];

    const benefits = [
        "Early access to premium features",
        "Exclusive beta testing opportunity",
        "Priority support and onboarding",
        "Special founding member pricing",
        "Shape product development",
        "First access to new features"
    ];

    return (
        <MainLayout>
            <div className="max-w-7xl mx-auto space-y-8 py-8">
                {/* Show analytics button only for specific user */}
                {!user?.is_anonymous && user?.email && (
                    <div className="text-center">
                        <button
                            onClick={handleAnalyticsToggle}
                            className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors"
                            aria-label="Toggle Analytics View"
                        >
                            {showAnalytics ? 'Hide Analytics' : 'View Analytics Dashboard'}
                        </button>
                    </div>
                )}

                {/* Render either Analytics Dashboard or Waitlist content */}
                {!user?.is_anonymous && user?.email && showAnalytics ? (
                    <AnalyticsDashboard />
                ) : (
                    <div className="max-w-4xl mx-auto">
                        {/* Hero Section */}
                        <div className="text-center space-y-4">
                            <h1 className="text-3xl font-bold text-gray-900">
                                Analytics Coming Soon
                            </h1>
                            <p className="text-lg text-gray-600">
                                Join our waitlist to be the first to access powerful subscription analytics
                            </p>
                        </div>

                        {/* Feature Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                            {features.map((feature, index) => (
                                <div key={index} className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow">
                                    <div className="flex flex-col items-center text-center space-y-3">
                                        {feature.icon}
                                        <h3 className="font-semibold text-gray-900">{feature.title}</h3>
                                        <p className="text-gray-600">{feature.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Benefits List */}
                        <div className="mt-8">
                            <h2 className="text-2xl font-bold text-gray-900">Benefits of Joining</h2>
                            <ul className="list-disc pl-8">
                                {benefits.map((benefit, index) => (
                                    <li key={index} className="text-gray-600">{benefit}</li>
                                ))}
                            </ul>
                        </div>

                        {/* Waitlist Form */}
                        <div className="mt-8 max-w-md mx-auto">
                            <WishlistButton featureId="analytics" />
                        </div>
                    </div>
                )}
            </div>
        </MainLayout>
    );
};